/**
 * Round selection and onset extraction. Framework-free.
 *
 * Turning a chart into a playable round is where the "is a 5-10s lead-in
 * enough" question gets operationalised: the lead-in has to end on a musical
 * boundary, not mid-word, or we're testing the player's reaction time instead
 * of their memory.
 */

import type { Chart, ChartWord, RoundSpec, ScoringConfig, Tier } from "@catchup/shared";

export interface RoundCandidate {
  spec: RoundSpec;
  sectionLabel: string;
  wordCount: number;
  leadInWords: string[];
  singWords: string[];
  firstLine: string;
}

/**
 * Build playable rounds from a chart.
 *
 * The lead-in ends at a word boundary with a gap after it, so the player hears
 * a complete phrase and then silence — rather than the audio cutting out
 * mid-syllable, which reads as a bug and destroys the "catch up" feeling.
 */
export function buildRounds(
  chart: Chart,
  config: ScoringConfig,
  tier: Tier,
): RoundCandidate[] {
  const out: RoundCandidate[] = [];
  const { leadInMin, leadInMax } = config.capture;

  for (const [sectionIndex, section] of chart.sections.entries()) {
    if (!section.playable) continue;
    const words = chart.words.filter((w) => w.section === sectionIndex);
    if (words.length < 8) continue;

    // Find a cut point: the largest inter-word gap inside the lead-in window.
    // A big gap means a phrase ended there.
    let cutIndex = -1;
    let bestGap = -1;
    for (let i = 0; i < words.length - 4; i++) {
      const elapsed = words[i]!.end - section.start;
      if (elapsed < leadInMin) continue;
      if (elapsed > leadInMax) break;
      const gap = (words[i + 1]!.start ?? Infinity) - words[i]!.end;
      if (gap > bestGap) {
        bestGap = gap;
        cutIndex = i;
      }
    }

    if (cutIndex < 0) {
      // No word boundary in the window — take the last word before leadInMax
      // rather than dropping the section entirely.
      for (let i = 0; i < words.length - 4; i++) {
        if (words[i]!.end - section.start <= leadInMax) cutIndex = i;
      }
    }
    if (cutIndex < 0 || cutIndex >= words.length - 4) continue;

    const singStart = words[cutIndex + 1]!.start;
    const singWords = words.slice(cutIndex + 1);
    if (singWords.length < 6) continue;

    const singEnd = singWords[singWords.length - 1]!.end;

    out.push({
      spec: {
        chartFileHash: chart.fileHash,
        leadInStart: section.start,
        singStart,
        singEnd,
        tier,
      },
      sectionLabel: section.label,
      wordCount: singWords.length,
      leadInWords: words.slice(0, cutIndex + 1).map((w) => w.text),
      singWords: singWords.map((w) => w.text),
      firstLine:
        chart.lines.find((l) => l.start >= singStart)?.text ??
        singWords.slice(0, 6).map((w) => w.text).join(" "),
    });
  }

  return out;
}

/** The words the player is expected to sing in this round. */
export function roundWords(chart: Chart, spec: RoundSpec): ChartWord[] {
  return chart.words.filter(
    (w) => w.start >= spec.singStart - 1e-6 && w.start <= spec.singEnd + 1e-6,
  );
}

/** Expected onsets, in track time. */
export function expectedOnsets(chart: Chart, spec: RoundSpec): number[] {
  return roundWords(chart, spec).map((w) => w.start);
}

/**
 * Convert mic onsets from AudioContext time into track time.
 *
 * Two corrections, and both matter:
 *   - subtract the round's start in context time, to get "seconds since the
 *     player took over", then re-add the track offset;
 *   - subtract the measured round-trip latency, because the onset was detected
 *     that much AFTER the player actually made the sound.
 *
 * Kept as a pure function so `scripts/retune.ts` can redo it offline from the
 * raw logged onsets — which is what makes a calibration bug recoverable
 * without asking anyone to sing again.
 */
export function toTrackTime(
  detectedRaw: number[],
  singStartContextTime: number,
  singStartTrackTime: number,
  latency: number,
): number[] {
  return detectedRaw.map(
    (t) => t - singStartContextTime - latency + singStartTrackTime,
  );
}

/** Lines to show for the faded tier, with the moment each should vanish. */
export function fadedSchedule(
  chart: Chart,
  spec: RoundSpec,
  config: ScoringConfig,
): Array<{ text: string; start: number; hideAt: number }> {
  return chart.lines
    .filter((l) => l.start >= spec.singStart - 2 && l.start <= spec.singEnd)
    .map((l) => ({
      text: l.text,
      start: l.start,
      hideAt: l.start - config.fadedLeadOut,
    }));
}
